import { isPVQForm } from 'helpers/form';
import { DEFAULT_EARLIEST, DEFAULT_LATEST } from 'constants/dateLimits';
import { itemsSeizedTypes } from 'constants/enums/financialOptions';
import currencyModelValidator from 'forms/shared/validators/models/currency';
import { LocationModel, ValidateJSModel } from 'forms/shared/types';
import { cityPattern, notPOBoxPattern, zipCodePattern } from 'constants/patterns';
import { usStatesValues } from 'constants/enums/usStates';
import { usTerritoriesPVQValues } from 'constants/enums/usTerritories';
import { hasYesOrNo } from 'models/validate';
import { setToStartOfMonth } from 'helpers/date';
import { FinancialAssetSeizedItemModel } from '../types';

const pvqAssetSeizedLocationValidator: ValidateJSModel<LocationModel> = {

  /* ------------------------------------------------------------------
  *  - The Asset Seized subsection is unique to PVQ so it is          -
  *  - unnecessary to check for formType isPVQ here.                  -
  *  ------------------------------------------------------------------ */

  street: {
    presence: true,
    format: { pattern: notPOBoxPattern, flags: 'i', message: 'error.location.street.poBox.pattern' },
  },

  street2: {},

  city: {
    presence: true,
    format: { pattern: cityPattern, message: 'error.city.pattern' },
    length: {
      minimum: 2,
      maximum: 100,
      message: 'error.city.length',
    },
  },

  state: {
    presence: true,
    inclusion: {
      within: [...usStatesValues, ...usTerritoriesPVQValues],
      message: 'error.state.notfound',
    },
  },

  zipcode: {
    presence: true,
    format: {
      pattern: zipCodePattern,
      message: 'error.zipcode.pattern',
    },
  },

  validated: {},

  country: {},

  countryComments: {},

  county: {},

  layout: {},
};

export const financialAssetSeizedItemValidator: ValidateJSModel<FinancialAssetSeizedItemModel> = {
  SeizureType: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        hasValue: {
          validator: {
            inclusion: {
              within: itemsSeizedTypes,
              message: 'error.assetSeized.seizureType',
            },
          },
        },
      };
    }

    return {};
  },

  SeizureTypeExplain: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.SeizureType?.value?.startsWith?.('Other')) {
      return {
        hasValue: true,
      };
    }

    return {};
  },

  Why: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        hasValue: true,
      };
    }

    return {};
  },

  When: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        date: {
          requireMonth: true,
          requireYear: true,
          requireDay: false,
          earliest: setToStartOfMonth(options?.applicantBirthdate || DEFAULT_EARLIEST),
          latest: DEFAULT_LATEST,
        },
      };
    }

    return {};
  },

  WhenEstimated: {},

  AmountOwed: {
    presence: true,
    model: {
      validator: currencyModelValidator,
    },
  },

  AmountOwedEstimated: {},

  Current: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        hasValue: {
          validator: hasYesOrNo,
        }
      };
    }

    return {};
  },

  WhenResolved: (value, attributes, attributeName, options) => (attributes?.Current?.value === 'No' ? {
    presence: true,
    date: {
      requireMonth: true,
      requireYear: true,
      requireDay: false,
      earliest: attributes?.When || setToStartOfMonth(options?.applicantBirthdate || DEFAULT_EARLIEST),
      tooEarly: 'error.date.financialDelinquencyResolvedDateTooEarly',
      latest: DEFAULT_LATEST,
    },

  } : {}),

  WhenResolvedEstimated: {},

  ActionsTaken: (value, attributes) => {
    if (attributes?.Current?.value === 'Yes' && attributes?.ActionsTakenNone?.applicable !== false) {
      return {
        hasValue: {
          validator: {
            length: {
              minimum: 1,
            }
          }
        },
      };
    }

    return {};
  },

  ActionsTakenNone: {},

  ActionsTakenReason: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.Current?.value === 'Yes' && attributes?.ActionsTakenNone?.applicable === false) {
      return {
        hasValue: true,
      };
    }

    return {};
  },

  CourtName: (value, attributes) => (attributes?.CourtNameNotApplicable?.applicable ? {
    hasValue: true,
  } : {}),

  CourtNameNotApplicable: {},
  
  OrderNum: (value, attributes) => (attributes?.OrderNumIdk?.applicable ? {
    hasValue: true,
  } : {}),

  OrderNumIdk: {},

  CourtLocation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        location: {
          validator: pvqAssetSeizedLocationValidator,
        },
      };
    }

    return {};
  },
};
